import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { AccountingInterfaceDetailTableModel } from 'src/app/models/tables/invoicing/accounting-interface-detail-table.model';
import { AccountingInterfaceTableModel } from 'src/app/models/tables/invoicing/accounting-interface-table.model';
import { environment } from 'src/environments/environment';
import { AccountingInterfaceDetailModel } from './../../models/invoicing/accounting-interface-detail.model';
import { AccountingInterfaceModel } from './../../models/invoicing/accounting-interface.model';
import { AccountingInterfaceDetailCaptureTableModel } from 'src/app/models/tables/invoicing/accounting-interface-detail-capture.model';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';

@Injectable({
  providedIn: 'root'
})
export class AccountingInterfaceService {

  constructor(private _httpClient: HttpClient) { }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetColumnsForGeneralConsultationAsync}`);
  }

  getAccountInterfacesForGeneralConsultation(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<AccountingInterfaceTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<AccountingInterfaceTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAccountInterfacesForGeneralConsultation}`,
      filters, { params: params }
    );
  }

  getAccountingInterfaceById(accountingInterfaceId: number): Observable<AccountingInterfaceModel> {
    return this._httpClient.get<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAccountingInterfaceById}/${accountingInterfaceId}`);
  }

  getAllBussinessDocumentsTypesForSelectAsync(branchId: number, enterpriseId: number): Observable<any[]> {
    const params = new HttpParams().set('branchId', branchId).set('enterpriseId', enterpriseId);
    return this._httpClient.get<any[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAllBussinessDocumentsTypesForSelectAsync}`,
      { params }
    );
  }

  getFirstBussinessDocumentsTypeAsync(branchId: number, enterpriseId: number): Observable<any> {
    const params = new HttpParams().set('branchId', branchId).set('enterpriseId', enterpriseId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetFirstBussinessDocumentsTypeAsync}`,
      { params }
    );
  }

  createAccountingInterface(accountingInterfaceContract: AccountingInterfaceModel): Observable<AccountingInterfaceModel> {
    return this._httpClient.post<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.CreateAccountingInterface}`,
      accountingInterfaceContract);
  }

  updateAccountingInterface(accountingInterfaceContract: AccountingInterfaceModel): Observable<AccountingInterfaceModel> {
    return this._httpClient.put<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.UpdateAccountingInterface}`,
      accountingInterfaceContract);
  }

  deleteAccountingInterface(accountingInterfaceId: number): Observable<AccountingInterfaceModel> {
    return this._httpClient.delete<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.DeleteAccountingInterface}/${accountingInterfaceId}`);
  }

  setStateAccountingInterface(accountingInterfaceId: number): Observable<AccountingInterfaceModel> {
    return this._httpClient.put<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.SetActiveAccountingInterface}/${accountingInterfaceId}`,
      null);
  }

  getAccountingInterfaceByKeysAsync(branchId: number, enterpriseId: number, consecutive: number): Observable<AccountingInterfaceModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId)
      .set('consecutive', consecutive);
    return this._httpClient.get<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAccountingInterfaceByKeys}`,
      { params: params });
  }

  getFirstAccountingInterfaceByKeysAsync(branchId: number, enterpriseId: number): Observable<AccountingInterfaceModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId);
    return this._httpClient.get<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetFirstAccountingInterfaceByKeys}`,
      { params: params });
  }

  getLastAccountingInterfaceByKeysAsync(branchId: number, enterpriseId: number): Observable<AccountingInterfaceModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId);
    return this._httpClient.get<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetLastAccountingInterfaceByKeys}`,
      { params: params });
  }

  getPreviusAccountingInterfaceByKeysAsync(branchId: number, enterpriseId: number, consecutive: number | null): Observable<AccountingInterfaceModel> {
    let params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId);
    if (consecutive !== null)
      params = params.set('consecutive', consecutive);
    return this._httpClient.get<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetPreviusAccountingInterfaceByKeys}`,
      { params: params });
  }

  getNextAccountingInterfaceByKeysAsync(branchId: number, enterpriseId: number, consecutive: number | null): Observable<AccountingInterfaceModel> {
    let params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId);
    if (consecutive !== null)
      params = params.set('consecutive', consecutive);
    return this._httpClient.get<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetNextAccountingInterfaceByKeys}`,
      { params: params });
  }

  //Servicios del detalle:
  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetColumnsForDetailConsultationAsync}`);
  }

  getColumnsForDetailCapture(isFilter: boolean): Observable<ColumnResponseModel[]> {
    const params = new HttpParams()
      .set('isFilter', isFilter);
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetColumnsForDetailCaptureAsync}`,
      { params: params }
    );
  }

  getAccountingInterfaceDetailsForDetailConsultation(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<AccountingInterfaceDetailTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<AccountingInterfaceDetailTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAccountingInterfaceDetailsForDetailConsultation}`,
      filters, { params: params }
    );
  }

  getAccountingInterfaceDetailById(accountingInterfaceId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean): Observable<PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>> {
    const params = new HttpParams()
      .set('accountingInterfaceId', accountingInterfaceId)
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.get<PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAccountingInterfaceDetailById}`,
      { params: params }
    );
  }

  getAccountingInterfaceDetailForSelection(accountingInterfaceId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>> {
    const params = new HttpParams()
      .set('accountingInterfaceId', accountingInterfaceId)
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<AccountingInterfaceDetailCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.GetAccountingInterfaceDetailForSelection}`,
      filters, { params: params }
    );
  }

  addAccountingInterfaceDetails(accountingInterfaceId: number, accountingInterfaceDetails: Array<AccountingInterfaceDetailModel>): Observable<AccountingInterfaceModel> {
    return this._httpClient.post<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.AddAccountingInterfaceDetails}/${accountingInterfaceId}`,
      accountingInterfaceDetails);
  }

  removeAccountingInterfaceDetails(accountingInterfaceId: number, accountingInterfaceDetails: number[]): Observable<AccountingInterfaceModel> {
    return this._httpClient.put<AccountingInterfaceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.RemoveAccountingInterfaceDetails}/${accountingInterfaceId}`,
      accountingInterfaceDetails);
  }

  exportCxc(accountingInterfaceId: number): Observable<Blob> {
    return this._httpClient.get<Blob>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.ExportCxc}/${accountingInterfaceId}`,
      { responseType: "blob" as "json" });
  }

  exportAccounting(accountingInterfaceId: number): Observable<Blob> {
    return this._httpClient.get<Blob>(`${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.ExportAccounting}/${accountingInterfaceId}`,
      { responseType: "blob" as "json" });
  }

  sendCountableMovement(accountingInterfaceId: number): Observable<PreInvoicingAlertModel[]> {
    return this._httpClient.post<PreInvoicingAlertModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.accountingInterface.SendCountableMovement}/${accountingInterfaceId}`,
      null
    );
  }

}
